import { type ComponentProps, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { assertEvent } from 'xstate5';

import { type NoticeBanner, useNoticeBannersStackContext } from '@sg/garnish';

import {
  type CustomizationMachineImplementations,
  type CustomizationMachineTypes,
  findFirstUnmetGroupLimits,
  getOutOfStockModificationNames,
} from '../../machine';

// ───────────────────────────────────────────────────────────────────────────────

/**
 * A custom hook that returns external actions for the customization machine.
 * It is used to add notice banners and fire optional callbacks.
 */
export const useCustomizationExternalActions = (
  callbacks: Callbacks,
  options?: Options,
): CustomizationExternalActions => {
  const { noticeBannerProps } = options ?? {};

  const { push: pushNoticeBanner, clear: clearNoticeBanners } = useNoticeBannersStackContext();

  const { formatMessage } = useIntl();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const addNoticeBanner = useCallback(
    (bannerProps: AddNoticeBannerProps) => {
      clearNoticeBanners();
      pushNoticeBanner(
        {
          ...bannerProps,
          ...noticeBannerProps,
          palette: 'neutral',
          autoHideTimeout: 3000,
        },
        true,
      );
    },
    [clearNoticeBanners, noticeBannerProps, pushNoticeBanner],
  );

  // ─── Actions ─────────────────────────────────────────────────────────

  const onOutOfStockIngredientsFound = useCallback<OnOutOfStockIngredientsFoundAction>(
    (args) => {
      const { context } = args;
      const { modifications, restaurantSlug, product } = context;

      const initialModifications = modifications?.initial ?? [];
      const outOfStockIngredients = getOutOfStockModificationNames(initialModifications); // prettier-ignore

      callbacks.onOutOfStockIngredientsFound?.({
        restaurant: restaurantSlug,
        product: product?.slug,
        ingredient: outOfStockIngredients,
      });
    },
    [callbacks],
  );

  const onMaxModificationsExceeded = useCallback<OnMaxModificationsExceededAction>(() => {
    const text = formatMessage(messages.maxModificationsExceeded);

    addNoticeBanner({ id: 'max-exceeded', text });

    callbacks.onMaxModificationsExceeded?.();
  }, [addNoticeBanner, formatMessage, callbacks]);

  const onMaxKindQtyExceeded = useCallback<OnMaxKindQtyExceededAction>(
    (args) => {
      assertEvent(args.event, [
        'customization.add-modification',
        'customization.toggle-split-portion',
      ]);

      const { allModificationsMap } = args.context;
      const { ingredientId } = args.event;

      const modification = allModificationsMap?.get(ingredientId);
      const kind = modification?.ingredient?.kind?.toLowerCase();
      const text = formatMessage(messages.maxKindQtyExceeded, { kind });

      addNoticeBanner({ id: 'max-kind-qty-exceeded', text });
    },
    [addNoticeBanner, formatMessage],
  );

  const onMaxKindExceeded = useCallback<OnMaxKindExceededAction>(
    (args) => {
      assertEvent(args.event, [
        'customization.add-modification',
        'customization.toggle-split-portion',
      ]);

      const { allModificationsMap, extrasModificationsMap, modificationLimits } = args.context;
      const { ingredientId } = args.event;

      const modification = allModificationsMap?.get(ingredientId);
      const groupDisplayName = modification?.modificationGroupDisplayName?.toLowerCase(); // prettier-ignore
      const groupName = modification?.modificationGroupName;
      const kindLimit = groupName ? modificationLimits[groupName] : undefined;

      const { isUpsellPricingEnabled } = args.context;

      const extrasModification =
        isUpsellPricingEnabled && modification?.ingredientId
          ? extrasModificationsMap.get(modification.ingredientId)
          : undefined;
      const extrasLimit = extrasModification
        ? modificationLimits[extrasModification.modificationGroupName]
        : undefined;

      const maxLimit =
        (kindLimit?.maxAggregateQuantity ?? 0) + (extrasLimit?.maxAggregateQuantity ?? 0);
      const hasMetadata = kindLimit !== undefined;

      const text = hasMetadata
        ? formatMessage(messages.maxKindLimitExceeded, {
            kind: groupDisplayName,
            max_limit: maxLimit,
          }) // prettier-ignore
        : formatMessage(messages.maxKindLimitExceededRaw);

      addNoticeBanner({ id: 'max-kind-limit-exceeded', text });

      callbacks.onMaxKindExceeded?.({ groupName: groupDisplayName });
    },
    [addNoticeBanner, formatMessage, callbacks],
  );

  const onMinUnmet = useCallback<OnMinUnmetAction>(
    (args) => {
      const { context } = args;
      const { modifications, modificationLimits } = context;

      if (!modifications || !modificationLimits) {
        addNoticeBanner({ id: 'min-unmet', text: formatMessage(messages.minUnmetRaw) }); // prettier-ignore

        return;
      }

      const { active } = modifications;

      const firstUnmetGroupLimits = findFirstUnmetGroupLimits(active, modificationLimits); // prettier-ignore
      const groupDisplayName = firstUnmetGroupLimits?.displayName.toLowerCase(); // prettier-ignore
      const minLimit = firstUnmetGroupLimits?.minAggregateQuantity;
      const hasMetadata = groupDisplayName !== undefined && minLimit !== undefined; // prettier-ignore

      const text = hasMetadata
        ? formatMessage(messages.minUnmet, { kind: groupDisplayName, kind_min_limit: minLimit }) // prettier-ignore
        : formatMessage(messages.minUnmetRaw);

      addNoticeBanner({
        id: 'min-unmet',
        text,
        highlights: hasMetadata ? [groupDisplayName, String(minLimit)] : [],
      });
    },
    [addNoticeBanner, formatMessage],
  );

  const onModificationAdded = useCallback<OnModificationAddedAction>(
    (args) => {
      assertEvent(args.event, [
        'customization.add-modification',
        'customization.dressing.increase-portions',
      ]);

      const { context, event } = args;
      const { ingredientId } = event;
      const { allModificationsMap } = context;

      const modification = allModificationsMap?.get(ingredientId);

      callbacks.onModificationAdded?.({ ingredient: modification?.ingredient?.name }); // prettier-ignore
    },
    [callbacks],
  );

  const onModificationRemoved = useCallback<OnModificationRemovedAction>(
    (args) => {
      assertEvent(args.event, [
        'customization.remove-modification',
        'customization.remove-modification-completely',
        'customization.dressing.decrease-portions',
        'customization.remove-split-portion-completely',
      ]);

      const { context, event } = args;
      const { ingredientId } = event;
      const { allModificationsMap } = context;

      const modification = allModificationsMap?.get(ingredientId);

      callbacks.onModificationRemoved?.({ ingredient: modification?.ingredient?.name }); // prettier-ignore
    },
    [callbacks],
  );

  const onCustomizationEnd: OnCustomizationEndAction | undefined = callbacks.onCustomizationEnd;

  const onCustomizationCancel: OnCustomizationCancelAction | undefined =
    callbacks.onCustomizationCancel;

  const onCustomizationComplete = useCallback<OnCustomizationCompleteAction>(
    (args) => {
      const { context } = args;

      const {
        product,
        productRestaurant: restaurant,
        lineItemId,
        quantity,
        modifications,
        mixedDressingDetails,
        netPrice,
      } = context;

      const productCost = product?.cost ?? 0;
      const totalCost = netPrice + productCost;

      callbacks.onCustomizationComplete?.({
        product,
        restaurant,
        quantity,
        modifications,
        mixedDressingDetails,
        totalCost,
        lineItemId,
      });
    },
    [callbacks],
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    onOutOfStockIngredientsFound,
    onMaxModificationsExceeded,
    onMaxKindQtyExceeded,
    onMaxKindExceeded,
    onMinUnmet,
    onModificationAdded,
    onModificationRemoved,
    onCustomizationEnd,
    onCustomizationCancel,
    onCustomizationComplete,
  };
};

// ─── Messages ──────────────────────────────────────────────────────────────────

const messages = defineMessages({
  maxModificationsExceeded: {
    defaultMessage: "You've reached the maximum amount of modifications.",
    description: 'Customization | Message shown when maximum number of modifications is exceeded',
  },
  maxKindLimitExceeded: {
    defaultMessage: "You've selected the maximum amount of {kind} ({max_limit})",
    description: 'Customization | Message shown when maximum amount of a specific kind is exceeded',
  },
  maxKindLimitExceededRaw: {
    defaultMessage: "You've selected the maximum amount of the selected kind",
    description:
      'Customization | Message shown when maximum amount of a specific kind is exceeded (raw)',
  },
  maxKindQtyExceeded: {
    defaultMessage: `{kind, select,
        base {You may only have one of each base.}
        topping {You may only have one of each topping.}
        premium {You may only have one of each premium.}
        dressing {You may only have one of each dressing.}
        other {The selected kind does not allow multiple quantities.}
      }`,
    description:
      'Customization | Message shown when trying to add multiple quantities of a kind that only allows one',
  },
  minUnmet: {
    defaultMessage: 'Hey, you need at least {kind_min_limit} selection from {kind}!',
    description:
      'Customization | Message shown when minimum required selections for a kind is not met',
  },
  minUnmetRaw: {
    defaultMessage: 'Hey, you need to add more ingredients!',
    description:
      'Customization | Message shown when minimum required selections for a kind is not met (raw)',
  },
});

// ───── Types ───────────────────────────────────────────────────────────────────

type OnOutOfStockIngredientsFoundAction = NonNullable<
  CustomizationExternalActions['onOutOfStockIngredientsFound']
>;
type OnMaxModificationsExceededAction = NonNullable<
  CustomizationExternalActions['onMaxModificationsExceeded']
>;
type OnMaxKindQtyExceededAction = NonNullable<CustomizationExternalActions['onMaxKindQtyExceeded']>;
type OnMaxKindExceededAction = NonNullable<CustomizationExternalActions['onMaxKindExceeded']>;
type OnMinUnmetAction = NonNullable<CustomizationExternalActions['onMinUnmet']>;
type OnModificationAddedAction = NonNullable<CustomizationExternalActions['onModificationAdded']>;
type OnModificationRemovedAction = NonNullable<
  CustomizationExternalActions['onModificationRemoved']
>;
type OnCustomizationEndAction = NonNullable<CustomizationExternalActions['onCustomizationEnd']>;
type OnCustomizationCancelAction = NonNullable<
  CustomizationExternalActions['onCustomizationCancel']
>;
type OnCustomizationCompleteAction = NonNullable<
  CustomizationExternalActions['onCustomizationComplete']
>;
type CustomizationExternalActions = NonNullable<CustomizationMachineImplementations['actions']>;

// ───────────────────────────────────────────────────────────────────────────────

type Callbacks = {
  onOutOfStockIngredientsFound?: (args: {
    restaurant: string;
    product: string | undefined;
    ingredient: string;
  }) => void;
  onMaxModificationsExceeded?: () => void;
  onMaxKindExceeded?: (args: { groupName: string | undefined }) => void;
  onModificationAdded?: (args: { ingredient: string | undefined }) => void;
  onModificationRemoved?: (args: { ingredient: string | undefined }) => void;
  onCustomizationEnd?: () => void;
  onCustomizationCancel?: () => void;
  onCustomizationComplete?: (params: {
    product: CustomizationMachineTypes.Context['product'];
    lineItemId: CustomizationMachineTypes.Context['lineItemId'];
    restaurant: CustomizationMachineTypes.Context['productRestaurant'];
    quantity: CustomizationMachineTypes.Context['quantity'];
    modifications: CustomizationMachineTypes.Context['modifications'];
    mixedDressingDetails: CustomizationMachineTypes.Context['mixedDressingDetails'];
    totalCost: number;
  }) => void;
};

type Options = {
  noticeBannerProps?: Partial<AddNoticeBannerProps>;
};

type AddNoticeBannerProps = Readonly<{
  id: string;
}> &
  Pick<
    ComponentProps<typeof NoticeBanner>,
    'text' | 'highlights' | 'autoHideTimeout' | 'ContentComponent' | 'style'
  >;
